import Session from '@/store/session'
import React from 'react'
import { Game, getApiPath, getStoredValue, removedStoredValue } from './common'
import { ProfileApiExport, ProfileApiImport, SearchResults, UserProfile } from './types/api'

const tokenVar = 'd2p-token'

export class Api {
  private dispatch: Session['dispatch'] | undefined
  private game = Game

  constructor(sessionDispatch?: Session['dispatch']) {
    this.dispatch = sessionDispatch
  }

  async updateLoginFrontend() {
    const url = new URL(getApiPath(`users/whoami`), window.location.href)

    const result = await this.fetchAuth(url, {
      method: 'GET'
    })

    if (result && result.userId) {
      this.dispatch?.setUser({
        id: result.userId,
        username: result.userName,
        active: result.userLevel || 1
      })
    }

    return result.userId as number
  }

  authHeaders() {
    const token = getStoredValue(tokenVar, () => getStoredValue(tokenVar), true)
    if (token) {
      return {
        Authorization: `Bearer ${token}`
      }
    }
  }

  signout() {
    removedStoredValue(tokenVar)
    removedStoredValue(tokenVar, true)
    this.dispatch?.setUser(undefined)
  }

  async fetchAuth(url: string | URL, options: RequestInit) {
    const response = await fetch(url.toString(), {
      ...options,
      credentials: 'include',
      headers: { ...options.headers, ...this.authHeaders() }
    }).catch(() => null)
    if (!response) {
      return { error: 'No response.' }
    }
    if (response && response.status === 401) {
      this.signout()
      return { error: 'Unauthorized.' }
    }
    const result = await response.json().catch(() => null)
    if (!response?.ok) {
      return { error: result?.error || 'Unknown error.' }
    }
    return result
  }

  async save(data: ProfileApiExport) {
    const url = new URL(getApiPath(`profiles/${this.game}`), window.location.href)

    const result = await this.fetchAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' }
    })

    if (!result || !result.id) {
      throw Error(result?.error || `Failed to save profile.`)
    }

    return result.id as string
  }

  async load(id: string) {
    const result = await fetch(getApiPath(`profiles/${this.game}/${id}`), {
      method: 'GET'
    })
      .then((response) => response.json())
      .catch(() => null)

    if (!result || result.error || !result.data) {
      throw Error(result?.error || `Unknown error`)
    }

    if (typeof result.date === 'string') {
      result.date = Date.parse(result.date)
    }

    if (result.userRating && typeof result.userRating === 'object') {
      result.userRating = result.userRating.rating
    }

    return result as ProfileApiImport
  }

  async delete(id: string) {
    const result = await this.fetchAuth(getApiPath(`profiles/${id}`), {
      method: 'DELETE'
    })

    if (!result || result.code !== 'OK') {
      throw Error(result?.error || `Failed to delete profile.`)
    }

    return true
  }

  async profiles() {
    const url = new URL(getApiPath(`search/legacy/${this.game}`), window.location.href)

    //if (params) url.search = new URLSearchParams(params).toString()

    const result = await this.fetchAuth(url, {
      method: 'GET'
    })

    if (!result || !Array.isArray(result)) {
      throw Error(result?.error || `Query failed.`)
    }

    return result.map((row) => {
      row = { ...row }
      if (typeof row.date === 'string') {
        row.date = Date.parse(row.date)
      }
      if (row.value !== undefined) {
        row.name = row.value
        delete row.value
      }
      return row
    }) as UserProfile[]
  }

  async search(params: {
    q?: string
    class?: string
    mainset?: string
    category?: string
    season?: string
    sort?: string
  }) {
    const url = new URL(getApiPath(`search/${this.game}`), window.location.href)
    url.search = new URLSearchParams({
      sort: 'total_ratings:desc,average_rating:desc,date:desc',
      ...params
    }).toString()

    const result = await this.fetchAuth(url, {
      method: 'GET'
    })

    if (!result || !('hits' in result)) {
      throw Error(result?.error || `Query failed.`)
    }

    result.hits = result.hits.map((row: any) => {
      row = { ...row }
      if (typeof row.date === 'string') {
        row.date = Date.parse(row.date)
      }
      return row
    })

    return result as SearchResults
  }

  async rate(id: string, rating: number) {
    const url = new URL(getApiPath(`rating/${id}/${rating}`), window.location.href)
    const result = await this.fetchAuth(url, {
      method: 'POST'
    })
    if (!result || result.code !== 'OK') {
      throw Error(result?.error || `Failed to rate profile`)
    }
    return true
  }

  async folders(action?: 'get'): Promise<any>
  async folders(action: 'save', params: any): Promise<boolean>
  async folders(action: 'delall', params: { from: number }): Promise<boolean>
  async folders(action: 'moveall', params: { from: number; to: number }): Promise<boolean>
  async folders(action: 'move', params: { id: string; to: number }): Promise<boolean>
  async folders(action: 'rename', params: { id: string; name: string }): Promise<boolean>
  async folders(action: string | undefined, params?: any) {
    if (action === 'save') {
      const result = await this.fetchAuth(getApiPath(`folders/${this.game}`), {
        method: 'POST',
        body: JSON.stringify(params),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'get' || !action) {
      const result = await this.fetchAuth(getApiPath(`folders/${this.game}`), {
        method: 'GET'
      })
      if (!result || result.error) {
        throw Error(result?.error || `Failed to load folders.`)
      }
      return result
    } else if (action === 'delall') {
      const result = await this.fetchAuth(getApiPath(`profiles/by-folder/${this.game}/${params.from}`), {
        method: 'DELETE'
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'moveall') {
      const result = await this.fetchAuth(getApiPath(`profiles/move`), {
        method: 'POST',
        body: JSON.stringify({ from: params.from, to: params.to, game: this.game }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'move') {
      const result = await this.fetchAuth(getApiPath(`profiles/move`), {
        method: 'POST',
        body: JSON.stringify({ id: params.id, to: params.to, game: this.game }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else if (action === 'rename') {
      const result = await this.fetchAuth(getApiPath(`profiles/rename`), {
        method: 'POST',
        body: JSON.stringify({ id: params.id, name: params.name }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        throw Error(result?.error || `Failed to modify folders.`)
      }
      return true
    } else {
      throw Error('Unknown action.')
    }
  }

  async metadata(key: string): Promise<any>
  async metadata(key: string, data: any): Promise<void>
  async metadata(key: string, data?: any): Promise<any> {
    if (arguments.length > 1) {
      const result = await this.fetchAuth(getApiPath(`account/metadata/${key}`), {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.key !== key) {
        throw Error(result?.error || `Failed to update metadata.`)
      }
    } else {
      const result = await this.fetchAuth(getApiPath(`account/metadata/${key}`), {
        method: 'GET'
      })
      if (!result || result.error || !result.value) {
        throw Error(result?.error || `Failed to load metadata.`)
      }
      return JSON.parse(result.value)
    }
  }
}

export default function useApi() {
  const dispatch = Session.useDispatch()
  return React.useMemo(() => new Api(dispatch), [dispatch])
}
